import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TenantTable } from "@/components/tenants/TenantTable";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeftRight,
  Building2,
  Filter,
  Loader2,
  Plus,
  RefreshCw,
  ScanSearch,
  Search,
  X,
} from "lucide-react";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { formatDateRangeForAPI } from "@/lib/date-utils";
import { formatDate } from "@/lib/utils";
import { PermissionGate } from "@/components/common/PermissionGate";
import { useTenantList } from "@/hooks/useTenantQueries";
import {
  ALL_STATUSES,
  TENANT_STATUS_OPTIONS,
  useTenantFiltersStore,
} from "@/stores/tenantFiltersStore";
import {
  AGREEMENT_FILTER_OPTIONS,
  ALL_AGREEMENTS,
  agreementFilterLabel,
  parseAgreementFilter,
  type AgreementFilterValue,
} from "@/services/admin-agreement.service";
import type { TenantStatus } from "@/services/admin-tenant.service";
import { getPaginationItems } from "@/lib/pagination";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
import {
  TENANT_COLUMNS,
  useTenantColumnsStore,
} from "@/stores/tenantColumnsStore";

export function TenantListPage() {
  const navigate = useNavigate();

  // Filters, page and page size live in a persisted store, so the view an operator set up
  // survives a reload and a trip to a tenant's detail screen and back. Order is not among them —
  // the table has no sort control, so the backend's newest-first default is the only ordering.
  const { search, status, dateRange, agreement } = useTenantFiltersStore(
    (state) => state.filters,
  );
  const page = useTenantFiltersStore((state) => state.page);
  const pageSize = useTenantFiltersStore((state) => state.pageSize);
  const setFilter = useTenantFiltersStore((state) => state.setFilter);
  const setPage = useTenantFiltersStore((state) => state.setPage);
  const setPageSize = useTenantFiltersStore((state) => state.setPageSize);
  const clearCache = useTenantFiltersStore((state) => state.clearCache);
  const hasActiveFilters = useTenantFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  // The table subscribes to this store itself; the page only needs what the menu renders from.
  const hiddenColumns = useTenantColumnsStore((state) => state.hidden);
  const toggleColumn = useTenantColumnsStore((state) => state.toggle);
  const showAllColumns = useTenantColumnsStore((state) => state.showAll);

  // Only the half-typed term is local: committing per keystroke would re-query and write
  // sessionStorage per character.
  const [searchInput, setSearchInput] = useState(search);

  // Debounce free-text search.
  useEffect(() => {
    const timer = setTimeout(() => {
      // Guarded because this also runs on mount, where committing the restored value would be a
      // no-op write that still sends the operator back to page 1.
      if (searchInput !== search) {
        setFilter("search", searchInput);
      }
    }, 750);
    return () => clearTimeout(timer);
  }, [searchInput, search, setFilter]);

  // Both ends or neither: the picker never produces half a range, and half a range narrows
  // nothing while still rendering an active-filter chip.
  const { fromDate: createdFrom, toDate: createdTo } =
    dateRange.from && dateRange.to
      ? formatDateRangeForAPI(dateRange.from, dateRange.to, false)
      : { fromDate: undefined, toDate: undefined };

  const agreementFilter = parseAgreementFilter(agreement);

  // Build query params
  const queryParams = {
    page,
    limit: pageSize,
    search: search || undefined,
    status: status !== ALL_STATUSES ? status : undefined,
    createdFrom,
    createdTo,
    agreementType: agreementFilter?.type,
    agreementAccepted: agreementFilter?.accepted,
  };

  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useTenantList(queryParams);

  // A term typed but not yet committed: the debounce has not fired, so nothing is fetching and the
  // table still shows the previous result. The spinner in the search box reads from this.
  const isSearchPending = searchInput !== search;

  const tenants = response?.data || [];
  const totalPages = response?.pagination ? response.pagination.totalPages : 1;
  const currentPage = page;
  const totalItems = response?.pagination?.totalItems || 0;

  // Refresh is the screen's "start over": it drops every filter, the page size and the cached
  // copy in sessionStorage, then reloads. The search box holds its own copy of what is being
  // typed, so clearing the store alone would leave the old term sitting in the input.
  const handleRefresh = () => {
    setSearchInput("");
    clearCache();
    void refetch();
  };

  // Only take the screen over when there is genuinely nothing to show. A refetch that fails — a
  // token refresh losing a race, a blip on one filter change — used to replace the table AND the
  // whole filter bar with a "Retry" card, so the operator had to rebuild their filters to get back
  // to rows that were still on screen a moment earlier. With a response in hand the failure is
  // reported in a banner above the table instead, and Retry is one click away either way.
  if (error && !response) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-destructive-ink">Failed to load tenants</p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the card, not the viewport: `fixed inset-0` centred this on the screen, so
            on a short table it hovered in the blank space well below the rows it described.

            `!isLoading` keeps the two loading signals mutually exclusive. The first load renders
            skeleton rows AND set this pill going at the same time, which said "Updating..." over a
            table that had never shown anything. Skeletons while there is nothing to show, the pill
            only while refreshing rows the operator can still see. */}
        {isFetching && !isLoading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
            <div className="w-full sm:w-[360px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                {/* The debounce window has no isFetching to report, so the box says so itself:
                    a term typed but not yet committed leaves the table showing the previous
                    result, and without this the screen sits motionless for three quarters of a
                    second after every keystroke and reads as frozen. */}
                {isSearchPending && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
                <Input
                  placeholder="Search tenant ID, email, phone"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  // React withholds onChange while an IME composition is open — which is the
                  // whole time a Vietnamese Telex/VNI method is assembling a word — so the term
                  // only reached state once a space closed the composition, and the search
                  // appeared to need a trailing space to run at all.
                  onCompositionEnd={(e) =>
                    setSearchInput(e.currentTarget.value)
                  }
                  className="pl-10 pr-9"
                />
              </div>
            </div>

            {/* Sits directly after the search box rather than at the far end of the toolbar: it
                is the only control here that creates anything, and beside the input it reads as
                the screen's primary action instead of the last of five icons. h-8 keeps it level
                with the search, filter and column controls — a 36px button next to 32px ones reads
                as misaligned rather than emphasised, and shrink-0 stops the caption collapsing
                when the filter chips beside it fill the row. */}
            <PermissionGate roles="SuperAdmin">
              <Button
                onClick={() => navigate("/admin/tenants/new")}
                size="sm"
                className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                New Tenant
              </Button>
            </PermissionGate>

            {/* The screen's second action, and secondary to New Tenant on purpose: it changes an
                existing customer rather than creating one. Same height and placement rules as the
                button above. SuperAdmin-gated because the endpoint behind it is. */}
            <PermissionGate roles="SuperAdmin">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/tenants/move-vm")}
                size="sm"
                className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm"
              >
                <ArrowLeftRight className="h-4 w-4" />
                Move VM
              </Button>
            </PermissionGate>

            {/* Third action, and the one that is a repair rather than a routine job: it finds
                machines whose plan was retired, which the pricing engine then charges from the
                legacy component prices instead of the catalogue. Same height and placement rules
                as the two buttons above. */}
            <PermissionGate roles="SuperAdmin">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/tenants/product-line-audit")}
                size="sm"
                className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm"
              >
                <ScanSearch className="h-4 w-4" />
                Product Lines
              </Button>
            </PermissionGate>

            <div className="order-3 flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:self-center sm:justify-end">
              {search && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Search: {search}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      setFilter("search", "");
                    }}
                    aria-label="Clear search filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {status !== ALL_STATUSES && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Status: {status}
                  <button
                    type="button"
                    onClick={() => setFilter("status", ALL_STATUSES)}
                    aria-label="Clear status filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {dateRange.from && dateRange.to && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Created: {formatDate(dateRange.from)} –{" "}
                  {formatDate(dateRange.to)}
                  <button
                    type="button"
                    onClick={() => setFilter("dateRange", {})}
                    aria-label="Clear created date filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {agreement !== ALL_AGREEMENTS && (
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
                >
                  Commitment: {agreementFilterLabel(agreement)}
                  <button
                    type="button"
                    onClick={() => setFilter("agreement", ALL_AGREEMENTS)}
                    aria-label="Clear commitment filter"
                    className="rounded-sm hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>

            <Popover>
              {/* Icon only, so the accessible name has to come from somewhere: `title` gives the
                  hover tooltip, `aria-label` gives screen readers the same words. The comment sits
                  outside PopoverTrigger on purpose: `asChild` forwards to a single child. */}
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Filters"
                  aria-label="Filters"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="tenantStatusFilter">Tenant Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) =>
                      setFilter(
                        "status",
                        value as TenantStatus | typeof ALL_STATUSES,
                      )
                    }
                  >
                    <SelectTrigger id="tenantStatusFilter">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {TENANT_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tenantCreatedDateFilter">Created Date</Label>
                  <DateRangePicker
                    value={dateRange}
                    onChange={(range) => setFilter("dateRange", range)}
                    placeholder="Select date range"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tenantAgreementFilter">Commitment</Label>
                  <Select
                    value={agreement}
                    onValueChange={(value) =>
                      setFilter("agreement", value as AgreementFilterValue)
                    }
                  >
                    <SelectTrigger id="tenantAgreementFilter">
                      <SelectValue placeholder="All Commitments" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGREEMENT_FILTER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </PopoverContent>
            </Popover>

            <ColumnSettingsMenu
              columns={TENANT_COLUMNS}
              isVisible={(id) => !hiddenColumns.includes(id)}
              toggle={toggleColumn}
              showAll={showAllColumns}
              hiddenCount={hiddenColumns.length}
              className="h-8 min-h-8 px-2 hover:translate-y-0 active:translate-y-0"
            />
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}
          {isLoading ? (
            <TenantTable
              tenants={tenants}
              isLoading
              startIndex={(currentPage - 1) * pageSize}
            />
          ) : tenants.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              {/* Fills the card the way the table does instead of collapsing to its own
                  content. CardContent is a min-h-0 flex column, so a fixed-height block here
                  left the card ending part-way down the screen: the whole panel changed size
                  the moment a filter emptied the list, and changed back when it matched
                  again. justify-center then puts the message in the middle of the space the
                  rows occupied rather than tucked under the toolbar. */}
              <Building2 className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters ? "No tenants found" : "No tenants created"}
              </h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting or clearing your filters."
                  : "Tenants will appear here once created."}
              </p>
            </div>
          ) : (
            <TenantTable
              tenants={tenants}
              startIndex={(currentPage - 1) * pageSize}
            />
          )}

          {/* The footer sits outside the three branches above, so a search that matched nothing
              still shows its pager, its page-size picker and the row count. It reads
              "Page 1 of 1 (0 total)" with both arrows disabled — a true statement about a result
              that came back, and the row an operator needs to change the page size or step back a
              page after a filter emptied the list. Before, the whole footer lived inside the
              "has rows" branch, so a zero-result search lost the page-size picker with it.

              `!isLoading` is the one case still held back: on the very first load `response` is
              undefined, so "0 total" would describe a request that has not answered yet — the same
              lie the skeleton above exists to avoid. */}
          {!isLoading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalItems} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => setPageSize(parseInt(value))}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                {/* Rendered at every page count, not just `totalPages > 1`: hiding the pager on a
                    single page made the controls appear and disappear as filters changed. Previous
                    and Next disable themselves at the ends, so a single page simply shows both
                    greyed out next to page 1. */}
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                {/* Moved down from the toolbar to sit with the other paging controls, the same
                    place the resource and payment lists keep it. The caption "Refresh" is gone on
                    purpose: `handleRefresh` is this screen's "start over" — it drops every filter,
                    the page size and the cached copy in sessionStorage before reloading —
                    and a button captioned "Refresh" promises a reload while quietly emptying the
                    filter bar. With the caption gone this tooltip is the only thing left telling
                    the operator what the click actually does, so it says exactly that. */}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
